import React, { useState, useEffect, useCallback } from 'react';
import { ChevronUpDownIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import AuditLogTable, { AuditLogEntry } from '../../components/admin/AuditLogTable';
import { adminService, AuditLog } from '../../services/admin.service';

/**
 * AdminAuditPage: Panel de auditoría administrativo
 *
 * Funcionalidad:
 * - Carga auditoría con adminService.getAuditLog()
 * - Muestra tabla con AuditLogTable componente
 * - Filtros: cliente, acción, fecha, resultado
 * - Búsqueda por objetivo (tenant_id o service_id)
 * - Expandible para ver detalles completos
 * - Auto-refresh cada 30 seg (opcional)
 * - Export a CSV (fase 2)
 */

interface AuditFilters {
  tenantId?: string;
  action?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  searchQuery?: string;
}

interface PaginationState {
  limit: number;
  offset: number;
  total: number;
}

const AdminAuditPage: React.FC = () => {
  // State
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    limit: 50,
    offset: 0,
    total: 0,
  });
  const [filters, setFilters] = useState<AuditFilters>({});
  const [availableActions, setAvailableActions] = useState<string[]>([]);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30); // segundos

  /**
   * Carga los logs de auditoría desde la API
   */
  const fetchAuditLogs = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await adminService.getAuditLog({
        tenantId: filters.tenantId,
        action: filters.action,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        limit: pagination.limit,
      });

      if ('logs' in response) {
        // Normalizar y mapear a AuditLogEntry
        const normalizedLogs: AuditLogEntry[] = response.logs.map((log: AuditLog) => ({
          id: log.auditId,
          timestamp: new Date(log.timestamp),
          action: log.action,
          targetType: determineTargetType(log),
          targetId: log.resourceId || log.tenantId || 'N/A',
          targetName: getTargetName(log),
          actor: log.actor || 'System',
          result: normalizeStatus(log.status),
          errorMessage: log.details?.errorMessage,
          details: log.details,
          metadata: {
            auditId: log.auditId,
            tenantId: log.tenantId,
            resourceId: log.resourceId,
          },
        }));

        setAuditLogs(normalizedLogs);
        setPagination((prev) => ({
          ...prev,
          total: response.total,
        }));

        // Extraer acciones únicas para el filtro
        const actionSet = new Set(normalizedLogs.map((log) => log.action));
        const actions = Array.from(actionSet).sort();
        setAvailableActions(actions);
      } else {
        setError('Invalid response format from API');
      }
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError(err instanceof Error ? err.message : 'Failed to load audit logs');
    } finally {
      setIsLoading(false);
    }
  }, [filters, pagination.limit]);

  /**
   * Determina el tipo de objetivo (cliente, servicio, etc.)
   */
  const determineTargetType = (log: AuditLog): string => {
    const action = log.action?.toLowerCase() || '';

    if (action.includes('client') || action.includes('tenant')) {
      return 'Client';
    }
    if (action.includes('service') || action.includes('deployment')) {
      return 'Service';
    }
    if (action.includes('payment')) {
      return 'Payment';
    }
    if (action.includes('app')) {
      return 'App';
    }

    return 'Resource';
  };

  /**
   * Obtiene el nombre legible del objetivo
   */
  const getTargetName = (log: AuditLog): string => {
    if (log.details?.targetName) {
      return log.details.targetName;
    }
    if (log.details?.clientName) {
      return log.details.clientName;
    }
    if (log.details?.serviceName) {
      return log.details.serviceName;
    }
    return log.resourceId || log.tenantId || 'Unknown';
  };

  /**
   * Normaliza el estado de auditoría a formato de tabla
   */
  const normalizeStatus = (status: string): 'success' | 'warning' | 'error' => {
    if (!status) return 'warning';

    const statusLower = status.toLowerCase();
    if (statusLower === 'success' || statusLower === 'ok') return 'success';
    if (statusLower === 'failure' || statusLower === 'failed' || statusLower === 'error') return 'error';

    return 'warning';
  };

  /**
   * Efecto para cargar logs al montar o cambiar filtros
   */
  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  /**
   * Efecto para auto-refresh
   */
  useEffect(() => {
    if (!autoRefresh) return;

    const timer = setInterval(() => {
      fetchAuditLogs();
    }, refreshInterval * 1000);

    return () => clearInterval(timer);
  }, [autoRefresh, refreshInterval, fetchAuditLogs]);

  /**
   * Maneja cambios en filtros
   */
  const handleFilterChange = (newFilters: Partial<AuditFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
    }));

    // Resetear pagination al cambiar filtros
    setPagination((prev) => ({
      ...prev,
      offset: 0,
    }));
  };

  /**
   * Maneja cambios en la búsqueda
   */
  const handleSearchChange = (query: string) => {
    handleFilterChange({ searchQuery: query });
  };

  /**
   * Maneja cambios en paginación
   */
  const handlePaginationChange = (limit: number, offset: number) => {
    setPagination({
      limit,
      offset,
      total: pagination.total,
    });
  };

  /**
   * Filtra logs en el cliente según búsqueda
   */
  const filteredLogs = auditLogs.filter((log) => {
    if (!filters.searchQuery) return true;

    const query = filters.searchQuery.toLowerCase();
    return (
      log.targetId.toLowerCase().includes(query) ||
      (log.targetName?.toLowerCase().includes(query) ?? false) ||
      (log.actor?.toLowerCase().includes(query) ?? false)
    );
  });

  /**
   * Maneja el refresh manual
   */
  const handleManualRefresh = async () => {
    await fetchAuditLogs();
  };

  /**
   * Exporta logs a CSV (fase 2)
   */
  const handleExportCsv = () => {
    try {
      const headers = ['Timestamp', 'Action', 'Target Type', 'Target ID', 'Target Name', 'Actor', 'Result'];
      const rows = auditLogs.map((log) => [
        new Date(log.timestamp).toISOString(),
        log.action,
        log.targetType,
        log.targetId,
        log.targetName || '',
        log.actor,
        log.result,
      ]);

      const csv = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting CSV:', err);
      setError('Failed to export audit logs');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="py-6 sm:py-8 lg:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Auditoría</h1>
                <p className="mt-2 text-gray-600 dark:text-gray-400">
                  Registro de todas las actividades y cambios del sistema
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleManualRefresh}
                  disabled={isLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ReloadOutlined className={`${isLoading ? 'animate-spin' : ''}`} />
                  Actualizar
                </button>
                <button
                  onClick={handleExportCsv}
                  disabled={isLoading || auditLogs.length === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <DownloadOutlined />
                  Exportar CSV
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-700 dark:text-red-200">Error: {error}</p>
              </div>
            )}
          </div>

          {/* Filters Section */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search by Objective */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Buscar por objetivo
              </label>
              <input
                type="text"
                placeholder="tenant_id, service_id..."
                value={filters.searchQuery || ''}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Client/Tenant Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Cliente
              </label>
              <input
                type="text"
                placeholder="Filtrar por tenant_id..."
                value={filters.tenantId || ''}
                onChange={(e) => handleFilterChange({ tenantId: e.target.value || undefined })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Action Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Acción
              </label>
              <select
                value={filters.action || ''}
                onChange={(e) => handleFilterChange({ action: e.target.value || undefined })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todas las acciones</option>
                {availableActions.map((action) => (
                  <option key={action} value={action}>
                    {action}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Resultado
              </label>
              <select
                value={filters.status || ''}
                onChange={(e) => handleFilterChange({ status: e.target.value || undefined })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos los resultados</option>
                <option value="SUCCESS">Éxito</option>
                <option value="FAILURE">Error</option>
              </select>
            </div>
          </div>

          {/* Date Range Filter */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Desde
              </label>
              <input
                type="date"
                value={filters.dateFrom || ''}
                onChange={(e) => handleFilterChange({ dateFrom: e.target.value || undefined })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Hasta
              </label>
              <input
                type="date"
                value={filters.dateTo || ''}
                onChange={(e) => handleFilterChange({ dateTo: e.target.value || undefined })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Auto-Refresh Control */}
          <div className="mb-6 flex items-center gap-4 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Auto-actualizar cada
              </span>
            </label>
            {autoRefresh && (
              <input
                type="number"
                min="5"
                max="300"
                step="5"
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(Math.max(5, parseInt(e.target.value)))}
                className="w-20 px-3 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            )}
            {autoRefresh && (
              <span className="text-sm text-gray-600 dark:text-gray-400">segundos</span>
            )}
          </div>

          {/* Audit Log Table */}
          <AuditLogTable
            logs={filteredLogs}
            loading={isLoading}
          />

          {/* Footer Info */}
          <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm text-blue-700 dark:text-blue-200">
              Mostrando {filteredLogs.length} de {auditLogs.length} registros de auditoría.
              {pagination.total > pagination.limit && (
                <span className="ml-2">Total en base de datos: {pagination.total}</span>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAuditPage;
